<script>
    import { onMount } from "svelte";

    import {
        getEmailValidation,
        getLandlineValidation,
        getPhoneValidation,
        getText,
        goBack,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { user } from "../../stores";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import { editIcon } from "../../Utils/utilConstants.svelte";

    import {
        Button,
        CardLayout,
        PageHead,
        PageLoading,
    } from "../../Components/Common/index.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;

    $: isValidEmail = surgeryRoomDetails.contactEmail
        ? getEmailValidation(surgeryRoomDetails.contactEmail)
        : true;
    $: isValidPhone = surgeryRoomDetails.contactPhone
        ? getPhoneValidation(surgeryRoomDetails.contactPhone) ||
          getLandlineValidation(surgeryRoomDetails.contactPhone)
        : true;

    $: isSaveBtnDisabled = !isValidEmail || !isValidPhone;

    $: btnOptions = [
        {
            iconName: "editIcon",
            iFont: editIcon,
            iconFunction: () => (isEdit = true),
            hasTooltip: false,
            btnText: "Edit",
            isHidden: isEdit,
        },
    ];

    let isLoadingSurgeryRoom = false,
        surgeryRoomDetails = {},
        defaultSurgeryRoomDetails = {},
        isModifyingSurgeryRoom = false;

    let isEdit = false;

    onMount(() => loadSurgeryRoom());

    const loadSurgeryRoom = async () => {
        if (!params.id) goBack();

        isLoadingSurgeryRoom = true;

        await apiMethods.getMethod.getSurgeryRoomById(params.id).then((res) => {
            if (!res || res.error) {
                showError(
                    res.error ??
                        "Failed to load surgery room details, please refresh the page and try again and contact support if the issue persists.",
                );
                goBack();
                return;
            } else {
                surgeryRoomDetails = { ...res };
                defaultSurgeryRoomDetails = { ...res };
            }
        });
        // await apiMethods.getMethod.getSurgeryRooms(0, 999999).then((res) => {
        //     const target = res.results.find((r) => r.id == params.id);

        //     surgeryRoomDetails = { ...target };
        //     defaultSurgeryRoomDetails = { ...target };
        // });

        isLoadingSurgeryRoom = false;
    };

    const updateSurgeryRoom = async () => {
        isModifyingSurgeryRoom = true;

        await apiMethods.putMethod
            .updateSurgeryRoom(surgeryRoomDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update surgery room details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    surgeryRoomDetails = { ...res };
                    defaultSurgeryRoomDetails = { ...res };
                    isEdit = false;
                }
            });

        isModifyingSurgeryRoom = false;
    };

    const cancelEdit = () => {
        isEdit = false;
        surgeryRoomDetails = { ...defaultSurgeryRoomDetails };
    };

    // HELPER FUNCTIONS
</script>

{#if isLoadingSurgeryRoom}
    <PageLoading />
{:else}
    <PageHead title="View Surgery Room Details" />

    <CardLayout title="Surgery Room Details" {btnOptions}>
        <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
            <div class="flex flex-col flex-1 gap-y-4 h-auto">
                {#if isUserAdmin}
                    <div class="flex items-center gap-x-4">
                        <label for="group" class="w-1/4">Group</label>
                        <div class="flex items-center flex-1">
                            <p>{getText(surgeryRoomDetails.groupName, "-")}</p>
                        </div>
                    </div>
                {/if}

                <div class="flex items-center gap-x-4">
                    <label for="group" class="mandatory w-1/4">Surgeon</label>
                    <div class="flex items-center flex-1">
                        <p>{getText(surgeryRoomDetails.sugeonName, "-")}</p>
                    </div>
                </div>

                <div class="flex items-center gap-x-4">
                    <label for="contactName" class="w-1/4">Contact Name</label>
                    {#if isEdit}
                        <input
                            type="text"
                            name="contactName"
                            class="input input-sm input-bordered flex-1"
                            id="contactName"
                            bind:value={surgeryRoomDetails.contactName}
                        />
                    {:else}
                        <p class="flex-1">{surgeryRoomDetails.contactName}</p>
                    {/if}
                </div>

                <div class="flex items-center gap-x-4">
                    <label for="preferredName" class="w-1/4">
                        Preferred Name
                    </label>
                    {#if isEdit}
                        <input
                            type="text"
                            name="preferredName"
                            class="input input-sm input-bordered flex-1"
                            id="preferredName"
                            bind:value={surgeryRoomDetails.preferredName}
                        />
                    {:else}
                        <p class="flex-1">{surgeryRoomDetails.preferredName}</p>
                    {/if}
                </div>

                <div class="flex flex-col gap-y-1">
                    <div class="flex items-center gap-x-4">
                        <label for="contactEmail" class="w-1/4"
                            >Contact Email</label
                        >
                        {#if isEdit}
                            <input
                                type="email"
                                name="contactEmail"
                                class="input input-sm input-bordered flex-1"
                                id="contactEmail"
                                bind:value={surgeryRoomDetails.contactEmail}
                            />
                        {:else}
                            <p class="flex-1">
                                {surgeryRoomDetails.contactEmail}
                            </p>
                        {/if}
                    </div>

                    {#if !isValidEmail}
                        <div class="flex items-center gap-x-4">
                            <label for="" class="w-1/4" />
                            <p class="flex-1 text-error">
                                Invalid Email Address
                            </p>
                        </div>
                    {/if}
                </div>

                <div class="flex flex-col gap-y-1">
                    <div class="flex items-center gap-x-4">
                        <label for="contactPhone" class="w-1/4"
                            >Contact Phone</label
                        >
                        {#if isEdit}
                            <input
                                type="phone"
                                name="contactPhone"
                                class="input input-sm input-bordered flex-1"
                                id="contactPhone"
                                bind:value={surgeryRoomDetails.contactPhone}
                            />
                        {:else}
                            <p class="flex-1">
                                {surgeryRoomDetails.contactPhone}
                            </p>
                        {/if}
                    </div>

                    {#if !isValidPhone}
                        <div class="flex items-center gap-x-4">
                            <label for="" class="w-1/4" />
                            <p class="flex-1 text-error">
                                Invalid Contact Phone
                            </p>
                        </div>
                    {/if}
                </div>
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-auto">
                <div class="flex items-center gap-x-4">
                    <label for="location" class="w-1/4">Location</label>
                    {#if isEdit}
                        <input
                            type="text"
                            name="location"
                            class="input input-sm input-bordered flex-1"
                            id="location"
                            bind:value={surgeryRoomDetails.location}
                        />
                    {:else}
                        <p class="flex-1">{surgeryRoomDetails.location}</p>
                    {/if}
                </div>

                <div class="flex items-center gap-x-4">
                    <label for="city" class="w-1/4">City</label>
                    {#if isEdit}
                        <input
                            type="text"
                            name="city"
                            class="input input-sm input-bordered flex-1"
                            id="city"
                            bind:value={surgeryRoomDetails.city}
                        />
                    {:else}
                        <p class="flex-1">{surgeryRoomDetails.city}</p>
                    {/if}
                </div>

                <div class="flex items-center gap-x-4">
                    <label for="state" class="w-1/4">State</label>
                    {#if isEdit}
                        <input
                            type="text"
                            name="state"
                            class="input input-sm input-bordered flex-1"
                            id="state"
                            bind:value={surgeryRoomDetails.state}
                        />
                    {:else}
                        <p class="flex-1">{surgeryRoomDetails.state}</p>
                    {/if}
                </div>

                <div class="flex items-center gap-x-4">
                    <label for="postCode" class="w-1/4">Post Code</label>
                    {#if isEdit}
                        <input
                            type="text"
                            name="postCode"
                            class="input input-sm input-bordered flex-1"
                            id="postCode"
                            bind:value={surgeryRoomDetails.postCode}
                        />
                    {:else}
                        <p class="flex-1">{surgeryRoomDetails.postCode}</p>
                    {/if}
                </div>
            </div>
        </div>

        {#if isEdit}
            <div class="flex flex-1 items-center justify-end p-4 gap-4">
                <Button clickFunc={cancelEdit} manualClass="w-[64px] btn-error">
                    Cancel
                </Button>

                <Button
                    clickFunc={updateSurgeryRoom}
                    manualClass={`w-[64px] ${isSaveBtnDisabled ? "text-primary" : ""} ${isModifyingSurgeryRoom ? "loading" : ""}`}
                    isDisabled={isSaveBtnDisabled}
                >
                    {isModifyingSurgeryRoom ? "Updating" : "Update"}
                </Button>
            </div>
        {/if}
    </CardLayout>
{/if}
